// src/pages/.../ManageMembers/utils/exportAdherentsExcel.js

import * as XLSX from "xlsx";

import {
  adherentBelongsToGroup,
  formatDateForDisplay,
  getAdherentGroups,
  getGroupDojoName,
  getLocalDateFileSuffix,
  sanitizeFileName,
} from "./manageMembers.utils";

const EXPORT_COLUMNS = [
  "Identifiant",
  "Nom",
  "Prénom",
  "Date de naissance",
  "Genre",
  "N° licence",
  "Email",
  "Téléphone",
  "Adresse",
  "Code postal",
  "Commune",
  "Statut inscription",
  "Groupes",
  "Lieux",
  "Notes",
];

const COLUMN_WIDTHS = [
  { wch: 12 },
  { wch: 22 },
  { wch: 22 },
  { wch: 18 },
  { wch: 12 },
  { wch: 18 },
  { wch: 32 },
  { wch: 18 },
  { wch: 35 },
  { wch: 14 },
  { wch: 24 },
  { wch: 22 },
  { wch: 35 },
  { wch: 30 },
  { wch: 45 },
];

function buildAdherentRow(adherent, forcedGroup = null) {
  const groups = forcedGroup
    ? [forcedGroup]
    : getAdherentGroups(adherent);

  const groupNames = [
    ...new Set(
      groups
        .map((group) => group?.nom_groupe)
        .filter(Boolean),
    ),
  ];

  const dojoNames = [
    ...new Set(
      groups
        .map((group) => getGroupDojoName(group))
        .filter(
          (dojoName) =>
            dojoName && dojoName !== "Lieu non renseigné",
        ),
    ),
  ];

  return {
    Identifiant: adherent?.id ?? "",
    Nom: adherent?.nom || "",
    Prénom: adherent?.prenom || "",
    "Date de naissance": formatDateForDisplay(
      adherent?.date_naissance,
    ),
    Genre: adherent?.genre || "",
    "N° licence":
      adherent?.licence_number ||
      adherent?.numero_licence ||
      adherent?.licence ||
      "",
    Email:
      adherent?.email ||
      adherent?.mail_adherent ||
      "",
    Téléphone:
      adherent?.tel ||
      adherent?.tel_adherent ||
      "",
    Adresse: adherent?.adresse || "",
    "Code postal": adherent?.cp || "",
    Commune: adherent?.commune || "",
    "Statut inscription":
      adherent?.statut_inscription || "",
    Groupes: groupNames.join(", "),
    Lieux: dojoNames.join(", "),
    Notes: adherent?.notes || "",
  };
}

function createWorksheet(rows) {
  const worksheet = XLSX.utils.json_to_sheet(rows, {
    header: EXPORT_COLUMNS,
  });

  worksheet["!cols"] = COLUMN_WIDTHS;

  const lastRow = Math.max(rows.length + 1, 1);

  worksheet["!autofilter"] = {
    ref: `A1:O${lastRow}`,
  };

  return worksheet;
}

function sanitizeSheetName(value) {
  const sanitized = String(value || "Groupe")
    .replace(/[\\/?*[\]:]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return sanitized.slice(0, 31) || "Groupe";
}

function createUniqueSheetName(rawName, usedNames) {
  const baseName = sanitizeSheetName(rawName);

  let candidate = baseName;
  let suffixNumber = 2;

  while (usedNames.has(candidate.toLowerCase())) {
    const suffix = ` ${suffixNumber}`;
    candidate = `${baseName.slice(0, 31 - suffix.length)}${suffix}`;
    suffixNumber += 1;
  }

  usedNames.add(candidate.toLowerCase());

  return candidate;
}

function sortAdherents(adherents) {
  return [...adherents].sort((first, second) => {
    const firstLabel =
      `${first?.nom || ""} ${first?.prenom || ""}`.trim();

    const secondLabel =
      `${second?.nom || ""} ${second?.prenom || ""}`.trim();

    return firstLabel.localeCompare(secondLabel, "fr", {
      sensitivity: "base",
    });
  });
}

export function exportAdherentsForGroup({
  adherents,
  group,
  clubName,
}) {
  if (!group?.id) {
    throw new Error("Le groupe à exporter est introuvable.");
  }

  const groupAdherents = sortAdherents(
    (Array.isArray(adherents) ? adherents : []).filter(
      (adherent) =>
        adherentBelongsToGroup(adherent, group.id),
    ),
  );

  const rows = groupAdherents.map((adherent) =>
    buildAdherentRow(adherent, group),
  );

  const workbook = XLSX.utils.book_new();
  const worksheet = createWorksheet(rows);

  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    sanitizeSheetName(group.nom_groupe || "Groupe"),
  );

  const dateSuffix = getLocalDateFileSuffix();

  const fileName = [
    "adherents",
    sanitizeFileName(clubName || "club"),
    sanitizeFileName(group.nom_groupe || "groupe"),
    dateSuffix,
  ]
    .filter(Boolean)
    .join("-");

  XLSX.writeFile(workbook, `${fileName}.xlsx`, {
    compression: true,
  });

  return groupAdherents.length;
}

export function exportAllAdherentsByGroups({
  adherents,
  groups,
  clubName,
}) {
  const safeAdherents = sortAdherents(
    Array.isArray(adherents) ? adherents : [],
  );

  const safeGroups = [...(Array.isArray(groups) ? groups : [])].sort(
    (first, second) =>
      String(first?.nom_groupe || "").localeCompare(
        String(second?.nom_groupe || ""),
        "fr",
        {
          sensitivity: "base",
        },
      ),
  );

  const workbook = XLSX.utils.book_new();
  const usedSheetNames = new Set();

  const globalRows = safeAdherents.map((adherent) =>
    buildAdherentRow(adherent),
  );

  XLSX.utils.book_append_sheet(
    workbook,
    createWorksheet(globalRows),
    createUniqueSheetName("Tous les adhérents", usedSheetNames),
  );

  safeGroups.forEach((group) => {
    const groupAdherents = safeAdherents.filter((adherent) =>
      adherentBelongsToGroup(adherent, group.id),
    );

    const groupRows = groupAdherents.map((adherent) =>
      buildAdherentRow(adherent, group),
    );

    const sheetName = createUniqueSheetName(
      group?.nom_groupe || `Groupe ${group?.id}`,
      usedSheetNames,
    );

    XLSX.utils.book_append_sheet(
      workbook,
      createWorksheet(groupRows),
      sheetName,
    );
  });

  const dateSuffix = getLocalDateFileSuffix();

  const fileName = [
    "adherents",
    sanitizeFileName(clubName || "club"),
    "tous-les-groupes",
    dateSuffix,
  ]
    .filter(Boolean)
    .join("-");

  XLSX.writeFile(workbook, `${fileName}.xlsx`, {
    compression: true,
  });

  return safeAdherents.length;
}